const getPopupElement = (popup) => {
    return popup?.closest?.('.fr-popup') || popup;
};

const clickOriginalTableCommand = (popup, editor, cmd, param) => {
    const selector = param
        ? `.teo-table-original-buttons .fr-command[data-cmd="${cmd}"][data-param1="${param}"]`
        : `.teo-table-original-buttons .fr-command[data-cmd="${cmd}"]`;

    const original = popup.querySelector(selector);

    if (original) {
        original.dispatchEvent(
            new MouseEvent('mousedown', {
                bubbles: true,
                cancelable: true,
                view: window,
            })
        );

        original.dispatchEvent(
            new MouseEvent('mouseup', {
                bubbles: true,
                cancelable: true,
                view: window,
            })
        );

        original.dispatchEvent(
            new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
            })
        );

        return true;
    }

    editor.selection?.restore?.();

    param !== undefined
        ? editor.commands.exec(cmd, param)
        : editor.commands.exec(cmd);

    return true;
};

const getSelectedTableCell = (editor) => {
    const selectionEl = editor?.selection?.element?.();

    return selectionEl?.closest?.('td, th') || null;
};

const getSelectedTable = (editor) => {
    return (
        getSelectedTableCell(editor)?.closest?.('table') ||
        editor?.selection?.element?.()?.closest?.('table') ||
        null
    );
};

const updateTableSubmenuDirection = (popup, row) => {
    if (!popup || !row) return;

    const submenu = row.querySelector('.teo-table-submenu-inline');
    if (!submenu) return;

    const popupElement = getPopupElement(popup);
    const menu = popup.querySelector('.teo-table-settings-menu');

    if (!popupElement || !menu) return;

    submenu.classList.remove('teo-table-submenu-left');

    const popupRect = popupElement.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();

    const submenuWidth =
        submenu.offsetWidth ||
        submenu.scrollWidth ||
        menuRect.width ||
        260;

    const viewportPadding = 12;
    const availableSpaceRight = window.innerWidth - menuRect.right;
    const availableSpaceLeft = menuRect.left;

    const shouldOpenLeft =
        availableSpaceRight < submenuWidth + viewportPadding &&
        availableSpaceLeft > availableSpaceRight;

    submenu.classList.toggle('teo-table-submenu-left', shouldOpenLeft);

    requestAnimationFrame(() => {
        const submenuRect = submenu.getBoundingClientRect();

        if (submenuRect.right > window.innerWidth - viewportPadding) {
            submenu.classList.add('teo-table-submenu-left');
        }

        if (
            submenuRect.left < viewportPadding &&
            popupRect.right + submenuRect.width <
            window.innerWidth - viewportPadding
        ) {
            submenu.classList.remove('teo-table-submenu-left');
        }
    });
};

const updateAllTableSubmenuDirections = (popup) => {
    if (!popup) return;

    popup
        .querySelectorAll('.teo-table-settings-row')
        .forEach((row) => {
            updateTableSubmenuDirection(popup, row);
        });
};

const updateTableSubmenuActiveState = (editor, popup) => {
    const table = getSelectedTable(editor);
    const cell = getSelectedTableCell(editor);

    popup.querySelectorAll('[data-teo-table-param]').forEach((btn) => {
        const cmd = btn.getAttribute('data-teo-table-cmd');
        const param = btn.getAttribute('data-teo-table-param');

        btn.classList.remove('teo-table-settings-item_active');
        btn.querySelector('.teo-active-check')?.remove();

        let isActive = false;

        if (cmd === 'tableCellStyle' && cell) {
            isActive = cell.classList.contains(param);
        }

        if (cmd === 'tableStyle' && table) {
            isActive = table.classList.contains(param);
        }

        if (cmd === 'tableCellVerticalAlign' && cell) {
            const verticalAlign =
                cell.style?.verticalAlign ||
                window.getComputedStyle(cell).verticalAlign;

            isActive = verticalAlign === param;
        }

        if (cmd === 'tableCellHorizontalAlign' && cell) {
            const textAlign =
                cell.style?.textAlign ||
                window.getComputedStyle(cell).textAlign;

            isActive =
                textAlign === param ||
                (param === 'left' &&
                    (!textAlign || textAlign === 'start')) ||
                (param === 'right' && textAlign === 'end');
        }

        if (isActive) {
            btn.classList.add('teo-table-settings-item_active');

            btn.insertAdjacentHTML(
                'beforeend',
                `
                    <span class="teo-active-check ml-auto">
                        <i class="far fa-check text-[17px]"></i>
                    </span>
                `
            );
        }
    });
};

export const updateTablePopupDesign = (editor) => {
    const popup = editor?.popups?.get?.('table.edit')?.get?.(0);
    const t = window.i18n.t;

    if (!popup) return;

    const table = getSelectedTable(editor);
    if (!table) return;

    if (popup.dataset.teoTablePopupReady === 'true') {
        updateTableSubmenuActiveState(editor, popup);

        requestAnimationFrame(() => {
            updateAllTableSubmenuDirections(popup);
        });

        return;
    }

    popup.dataset.teoTablePopupReady = 'true';
    popup.classList.add('teo-table-popup');

    const tableMenuItems = [
        {
            label: t(
                'widgets.toolbar_button.table_settings.table_header'
            ),
            icon: '<i class="far fa-table text-[17px]"></i>',
            cmd: 'tableHeader',
        },
        {
            label: t(
                'widgets.toolbar_button.table_settings.table_footer'
            ),
            icon: '<i class="far fa-table text-[17px]"></i>',
            cmd: 'tableFooter',
        },
        {
            label: t('widgets.toolbar_button.table_settings.rows'),
            icon: '<i class="far fa-grip-lines text-[17px]"></i>',
            cmd: 'tableRows',
            arrow: true,
        },
        {
            label: t('widgets.toolbar_button.table_settings.columns'),
            icon: '<i class="far fa-columns text-[17px]"></i>',
            cmd: 'tableColumns',
            arrow: true,
        },
        {
            label: t('widgets.toolbar_button.table_settings.cells'),
            icon: '<i class="far fa-th-large text-[17px]"></i>',
            cmd: 'tableCells',
            arrow: true,
        },
        {
            label: t(
                'widgets.toolbar_button.table_settings.vertical_align'
            ),
            icon: '<i class="far fa-arrows-v text-[17px]"></i>',
            cmd: 'tableCellVerticalAlign',
            arrow: true,
        },
        {
            label: t(
                'widgets.toolbar_button.table_settings.horizontal_align'
            ),
            icon: '<i class="far fa-align-left text-[17px]"></i>',
            cmd: 'tableCellHorizontalAlign',
            arrow: true,
        },
        {
            label: t(
                'widgets.toolbar_button.table_settings.cell_style'
            ),
            icon: '<img src="/images/icons/color-wand.svg" class="!w-5" />',
            cmd: 'tableCellStyle',
            arrow: true,
        },
        {
            label: t(
                'widgets.toolbar_button.table_settings.table_properties'
            ),
            icon: '<i class="far fa-sliders-h text-[17px]"></i>',
            cmd: 'tableProperties',
            arrow: true,
        },
        {
            label: t(
                'widgets.toolbar_button.table_settings.cell_properties'
            ),
            icon: '<i class="far fa-cog text-[17px]"></i>',
            cmd: 'tableCellProperties',
            arrow: true,
        },
        {
            label: t(
                'widgets.toolbar_button.table_settings.remove_table'
            ),
            icon: '<img src="/images/icons/trash_icon_red.svg" class="!w-4" />',
            cmd: 'tableRemove',
            danger: true,
        },
    ];

    const submenus = {
        tableRows: [
            {
                label: t(
                    'widgets.toolbar_button.table_settings.row_above'
                ),
                icon: '<i class="far fa-arrow-up text-[17px]"></i>',
                param: 'above',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.row_below'
                ),
                icon: '<i class="far fa-arrow-down text-[17px]"></i>',
                param: 'below',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.delete_row'
                ),
                icon: '<img src="/images/icons/trash_icon_red.svg" class="!w-4" />',
                param: 'delete',
                danger: true,
            },
        ],
        tableColumns: [
            {
                label: t(
                    'widgets.toolbar_button.table_settings.column_before'
                ),
                icon: '<i class="far fa-arrow-left text-[17px]"></i>',
                param: 'before',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.column_after'
                ),
                icon: '<i class="far fa-arrow-right text-[17px]"></i>',
                param: 'after',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.delete_column'
                ),
                icon: '<img src="/images/icons/trash_icon_red.svg" class="!w-4" />',
                param: 'delete',
                danger: true,
            },
        ],
        tableCells: [
            {
                label: t(
                    'widgets.toolbar_button.table_settings.merge_cells'
                ),
                icon: '<i class="far fa-object-group text-[17px]"></i>',
                param: 'merge',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.vertical_split'
                ),
                icon: '<i class="far fa-columns text-[17px]"></i>',
                param: 'vertical-split',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.horizontal_split'
                ),
                icon: '<i class="far fa-grip-lines text-[17px]"></i>',
                param: 'horizontal-split',
            },
        ],
        tableCellVerticalAlign: [
            {
                label: t('widgets.toolbar_button.table_settings.top'),
                icon: '<i class="far fa-arrow-to-top text-[17px]"></i>',
                param: 'top',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.middle'
                ),
                icon: '<i class="far fa-arrows-v text-[17px]"></i>',
                param: 'middle',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.bottom'
                ),
                icon: '<i class="far fa-arrow-to-bottom text-[17px]"></i>',
                param: 'bottom',
            },
        ],
        tableCellHorizontalAlign: [
            {
                label: t('widgets.toolbar_button.table_settings.left'),
                icon: '<i class="far fa-align-left text-[17px]"></i>',
                param: 'left',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.center'
                ),
                icon: '<i class="far fa-align-center text-[17px]"></i>',
                param: 'center',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.right'
                ),
                icon: '<i class="far fa-align-right text-[17px]"></i>',
                param: 'right',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.justify'
                ),
                icon: '<i class="far fa-align-justify text-[17px]"></i>',
                param: 'justify',
            },
        ],
        tableCellStyle: [
            {
                label: t(
                    'widgets.toolbar_button.table_settings.highlighted'
                ),
                icon: '',
                param: 'fr-highlighted',
            },
            {
                label: t(
                    'widgets.toolbar_button.table_settings.thick'
                ),
                icon: '',
                param: 'fr-thick',
            },
        ],
    };

    const buttons = popup.querySelector('.fr-buttons');
    if (!buttons) return;

    const oldButtonsHtml = buttons.innerHTML;

    buttons.innerHTML = `
        <div class="teo-table-settings-menu">
            ${tableMenuItems
            .map((item) => {
                const submenu = submenus[item.cmd];

                return `
                        <div class="teo-table-settings-row">
                            <button
                                type="button"
                                class="
                                    teo-table-settings-item
                                    ${item.danger
                        ? 'teo-table-settings-item_danger'
                        : ''
                    }
                                "
                                data-teo-table-cmd="${item.cmd}"
                            >
                                <span class="teo-table-settings-icon">
                                    ${item.icon}
                                </span>

                                <span class="teo-table-settings-label">
                                    ${item.label}
                                </span>

                                ${item.arrow
                        ? '<i class="far fa-chevron-right text-grey-03 ml-auto"></i>'
                        : ''
                    }
                            </button>

                            ${submenu
                        ? `
                                        <div class="teo-table-submenu-inline">
                                            <div class="teo-table-submenu-list">
                                                ${submenu
                            .map(
                                (sub) => `
                                                            <button
                                                                type="button"
                                                                class="
                                                                    teo-table-settings-item
                                                                    ${sub.danger
                                        ? 'teo-table-settings-item_danger'
                                        : ''
                                    }
                                                                "
                                                                data-teo-table-cmd="${item.cmd}"
                                                                data-teo-table-param="${sub.param}"
                                                            >
                                                                <span class="teo-table-submenu-icon">
                                                                    ${sub.icon}
                                                                </span>

                                                                <span class="teo-table-submenu-label">
                                                                    ${sub.label}
                                                                </span>
                                                            </button>
                                                        `
                            )
                            .join('')}
                                            </div>
                                        </div>
                                    `
                        : ''
                    }
                        </div>
                    `;
            })
            .join('')}
        </div>

        <div
            class="teo-table-original-buttons"
            style="display: none;"
        >
            ${oldButtonsHtml}
        </div>
    `;

    updateTableSubmenuActiveState(editor, popup);

    popup
        .querySelectorAll('.teo-table-settings-row')
        .forEach((row) => {
            row.addEventListener('mouseenter', () => {
                updateTableSubmenuDirection(popup, row);
            });

            row.addEventListener('focusin', () => {
                updateTableSubmenuDirection(popup, row);
            });
        });

    popup
        .querySelectorAll('[data-teo-table-cmd]')
        .forEach((btn) => {
            btn.addEventListener('mousedown', (e) => {
                e.preventDefault();
                e.stopPropagation();

                const cmd = btn.getAttribute(
                    'data-teo-table-cmd'
                );

                const param = btn.getAttribute(
                    'data-teo-table-param'
                );

                if (!cmd) return;

                if (submenus[cmd] && !param) return;

                clickOriginalTableCommand(
                    popup,
                    editor,
                    cmd,
                    param || undefined
                );

                setTimeout(() => {
                    updateTableSubmenuActiveState(editor, popup);
                    updateAllTableSubmenuDirections(popup);

                    if (
                        ![
                            'tableRows',
                            'tableColumns',
                            'tableCells',
                            'tableCellVerticalAlign',
                            'tableCellHorizontalAlign',
                            'tableCellStyle',
                        ].includes(cmd)
                    ) {
                        editor.popups.hide('table.edit');
                    }
                }, 0);
            });
        });

    requestAnimationFrame(() => {
        updateAllTableSubmenuDirections(popup);
    });
};